import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association|schedule/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

function detectGoverningBasis(opt: string): 'constitution' | 'articles' | 'third_schedule' | null {
  const s = String(opt || '');
  if (/third\s*schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return null;
}

/**
 * MY — Resignation of Secretary (M32107) chatflow.
 *
 * 문서-뷰 Preset Studio:
 *   input-title → select-entity → confirm
 *   + person lookup (officer-role → list → detail)
 *   + constitution clause (AOD와 동일 — 옵션 선택/직접 입력)
 *   + address-input / address-list
 */
export const secretaryResignationFlow: ChatFlow = {
  id: 'secretary-resignation',
  taskCodeMatch: ['M32107'],
  categoryMatch: ['resignation of secretary', 'secretary resignation'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Resignation of Secretary**.\n\n` +
          `This preset prepares a document package (${docCount || 3} document(s)): ` +
          `Directors' Circular Resolution, Secretary Resignation Letter, and Section 58I.\n\n` +
          `Fill the live documents on the right. Use **Full Name ✨** to look up a resigning secretary, ` +
          `and **Clause / Article No. ✨** to pick the constitution clause (same as Appointment of Director).\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for this resignation.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (Resignation of Secretary).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Complete the resignation documents on the right — use **✨** on a secretary name ' +
        'or **Clause / Article No.** Click **Generate** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'select-clause',
      aiMessage:
        'Select a **Clause / Article No.** (same as Appointment of Director), or type the number yourself.',
      widget: 'clause-number',
      widgetConfig: {
        targetVarKey: 'constitution_clause_number',
        targetEntityIndex: 0,
        clauseOptions: [],
        clauseDefaultBasis: 'constitution',
        analysis: {
          success: true,
          placeholder: 'constitution_clause_number',
          sourceResolver: 'SELECT',
          officerRole: null,
          varKey: 'constitution_clause_number',
          varLabel: 'Clause / Article No.',
          confidence: 1,
          message: 'Select or enter Clause / Article No.',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage:
        'Person details loaded. The form has been pre-filled. You can also drag/click the fields below.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    {
      id: 'address-list',
      aiMessage: 'This person/entity has multiple addresses. Pick the one to use.',
      widget: 'address-options',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || '',
          company_name: entityName,
        },
        nextStepId: 'confirm',
      };
    }

    // 정관번호 — ChatClauseNumber 전용 위젯
    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      if (ctx.fieldGroup === 'constitution-clause' || ctx.key === 'constitution_clause_number') {
        const options = Array.isArray(ctx.clauseOptions)
          ? ctx.clauseOptions.map((o: any) => String(o ?? '').trim()).filter(Boolean)
          : [];
        return {
          messages: [
            {
              role: 'user',
              content: 'Looking up **Clause / Article No.**…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-clause',
          nextStepWidgetData: {
            targetVarKey: 'constitution_clause_number',
            clauseOptions: options,
            clauseDefaultBasis: ctx.defaultBasis || 'constitution',
            defaultValue: ctx.defaultValue || '',
            analysis: {
              success: true,
              placeholder: 'constitution_clause_number',
              sourceResolver: 'SELECT',
              officerRole: null,
              varKey: 'constitution_clause_number',
              varLabel: 'Clause / Article No.',
              confidence: 1,
              message: 'Select or enter Clause / Article No.',
              relatedFields: [],
              options,
            } as PlaceholderAnalysis,
          },
        };
      }

      // Company Name ✨ → entity 목록 (비서 목록 아님)
      if (
        ctx.key === 'company_name' ||
        ctx.lookupType === 'entity' ||
        (ctx.fieldGroup === 'resignation-secretary' && !ctx.defaultRole)
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Search and select a **company**…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-entity',
        };
      }

      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }
      const role = ctx.defaultRole || 'SECRETARY';
      return {
        messages: [
          {
            role: 'user',
            content: `Looking up **${role}** for resignation…`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'clause-number-select') {
      const pick = payload || {};
      const clauseNo = String(pick.clauseNumber || '').trim();
      const rawOption = String(pick.rawOption || '').trim();
      const basis =
        pick.governingBasis ||
        detectGoverningBasis(rawOption) ||
        (clauseNo ? 'constitution' : null);
      const label =
        basis === 'third_schedule'
          ? 'Third Schedule'
          : clauseNo
            ? `${basis === 'articles' ? 'Article' : 'Clause'} ${clauseNo}`
            : 'Clause';
      const formUpdates: Record<string, any> = {
        constitution_clause_number: basis === 'third_schedule' ? '' : clauseNo,
        _clauseSyncAt: Date.now(),
        _clauseRawOption: rawOption,
      };
      // 라디오 반영용 — 항상 명시 (없으면 formData에 basis 가 안 남아 문서 체크박스가 안 바뀜)
      if (basis) formUpdates._resignGoverningBasis = basis;
      return {
        messages: [
          {
            role: 'user',
            content: `Governing document: **${label}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates,
        nextStepId: 'confirm',
      };
    }

    // legacy: text-input value-submit on select-clause (fallback)
    if (eventName === 'value-submit' && context.currentStepId === 'select-clause') {
      const raw = typeof payload === 'string' ? payload : payload?.value || payload;
      const rawStr = String(raw || '').trim();
      if (!rawStr) return {};
      const clauseNo = parseClauseNo(rawStr);
      const basis = detectGoverningBasis(rawStr);
      const formUpdates: Record<string, any> = {
        constitution_clause_number: clauseNo || rawStr,
      };
      if (basis) formUpdates._resignGoverningBasis = basis;
      return {
        messages: [
          {
            role: 'user',
            content: `Clause / Article No.: **${clauseNo || rawStr}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates,
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected person: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    if (eventName === 'request-address-input') {
      return {
        messages: [
          { role: 'user', content: 'Enter an address.', timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [
          {
            role: 'user',
            content: `Choose an address for **${subject}**.`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${opt.label || opt.type}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
